// src/pages/SettingsPage.jsx
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../../supabaseClient';
import {
  Box,
  Container,
  Typography,
  Button,
  Paper,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  useTheme,
  alpha,
  Fade,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Link as MuiLink
} from '@mui/material';
import {
  Business as BusinessIcon,
  Phone as PhoneIcon,
  Home as HomeIcon,
  Person as PersonIcon,
  Settings as SettingsIcon,

  Save as SaveIcon,
  Group as GroupIcon,
  AdminPanelSettings as AdminIcon
} from '@mui/icons-material';
import CompanyInformation from './components/CompanyInformation';
import BusinessAddress from './components/BusinessAddress';
import ContactInformation from './components/ContactInformation';
import PrimaryContact from './components/PrimaryContact';

import BusinessSettings from './components/BusinessSettings';
import { UsersSettings, ProfileManagement } from './components';
import DeleteCompany from './components/DeleteCompany';
import { usePermissions } from '../../utils/permissions';

const requiredFields = {
  'company-info': ['name', 'legal_name', 'industry', 'business_type'],
  'business-address': ['address', 'city', 'state', 'zip_code', 'country'],
  'contact-info': ['phone', 'email', 'website'],
  'primary-contact': ['contact_person_name', 'contact_person_title', 'contact_person_phone', 'contact_person_email'],
  'business-settings': ['default_currency', 'default_payment_terms', 'invoice_prefix', 'quote_prefix']
};

const SettingsPage = ({ currentCompany, onCompanyUpdated }) => {
  const theme = useTheme();
  const { canEditPage, canAccessPage, userRole } = usePermissions();
  const [selectedCategory, setSelectedCategory] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [profileData, setProfileData] = useState({
    first_name: '',
    last_name: ''
  });
  
  const [companyInfo, setCompanyInfo] = useState({
    name: '',
    legal_name: '',
    industry: '',
    business_type: '',
    business_license: '',
    founded_date: '',
    employee_count: '',
    description: '',
    address: '',
    address_2: '',
    city: '',
    state: '',
    zip_code: '',
    country: '',
    phone: '',
    fax: '',
    email: '',
    website: '',
    contact_person_name: '',
    contact_person_title: '',
    contact_person_phone: '',
    contact_person_email: '',

    time_zone: 'America/New_York',
    default_currency: 'USD',
    default_payment_terms: 30,
    invoice_prefix: 'INV',
    quote_prefix: 'QUO'
  });

  const categories = [
    {
      id: 'user-management',
      label: 'User Management',
      icon: <GroupIcon />,
      description: 'Manage users, roles, and permissions'
    },
    {
      id: 'profile-management',
      label: 'Profile Management',
      icon: <PersonIcon />,
      description: 'Update your personal profile and avatar'
    },
    {
      id: 'company-info',
      label: 'Company Information',
      icon: <BusinessIcon />,
      description: 'Basic company details and information'
    },
    {
      id: 'business-address',
      label: 'Business Address',
      icon: <HomeIcon />,
      description: 'Physical location and mailing address'
    },
    {
      id: 'contact-info',
      label: 'Contact Information',
      icon: <PhoneIcon />,
      description: 'Phone, email, and website details'
    },
    {
      id: 'primary-contact',
      label: 'Primary Contact',
      icon: <PersonIcon />,
      description: 'Main point of contact person'
    },

    {
      id: 'business-settings',
      label: 'Business Settings',
      icon: <SettingsIcon />,
      description: 'Currency, payment terms, and preferences'
    },
    {
      id: 'delete-company',
      label: 'Company Control',
      icon: <AdminIcon />,
      description: 'Deactivate, reactivate, or delete company'
    }
  ].filter(category => {
    const categoryPermissionMap = {
      'company-info': 'company_information',
      'business-address': 'business_address', 
      'contact-info': 'contact_information',
      'primary-contact': 'primary_contact',

      'business-settings': 'business_settings'
    };
    
    // Special handling for company control - only super admin can access
    if (category.id === 'delete-company') {
      return userRole === 'super_admin';
    }
    
    // Special handling for user management - check if user has access to any user management pages
    if (category.id === 'user-management') {
      return canAccessPage('active_users') || canAccessPage('invitations') || canAccessPage('roles_permissions');
    }
    
    const permissionKey = categoryPermissionMap[category.id];
    if (permissionKey) {
      return canAccessPage(permissionKey);
    }
    return true;
  });

  // Validation function
  const validateCurrentSection = () => {
    const errors = {};
    const currentRequiredFields = requiredFields[selectedCategory] || [];
    
    currentRequiredFields.forEach(field => {
      if (!companyInfo[field] || companyInfo[field].toString().trim() === '') {
        errors[field] = 'This field is required';
      }
    });
    
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Check if save button should be disabled
  const isSaveDisabled = () => {
    if (selectedCategory === 'profile-management') {
      return !profileData.first_name?.trim() || !profileData.last_name?.trim();
    }
    
    const currentRequiredFields = requiredFields[selectedCategory] || [];
    return currentRequiredFields.some(field => !companyInfo[field] || companyInfo[field]?.toString().trim() === '');
  };

  // Check if current category has edit permissions
  const hasEditPermissionForCategory = () => {
    const categoryPermissionMap = {
      'company-info': 'company_information',
      'business-address': 'business_address', 
      'contact-info': 'contact_information',
      'primary-contact': 'primary_contact',

      'business-settings': 'business_settings'
    };
    
    const permissionKey = categoryPermissionMap[selectedCategory];
    return permissionKey ? canEditPage(permissionKey) : true;
  };

  useEffect(() => {
    if (currentCompany) {
      setCompanyInfo({
        name: currentCompany.name || '',
        legal_name: currentCompany.legal_name || '',
        industry: currentCompany.industry || '',
        business_type: currentCompany.business_type || '',
        business_license: currentCompany.business_license || '',
        founded_date: currentCompany.founded_date || '',
        employee_count: currentCompany.employee_count || '',
        description: currentCompany.description || '',
        address: currentCompany.address || '',
        address_2: currentCompany.address_2 || '',
        city: currentCompany.city || '',
        state: currentCompany.state || '',
        zip_code: currentCompany.zip_code || '',
        country: currentCompany.country || '',
        phone: currentCompany.phone || '',
        fax: currentCompany.fax || '',
        email: currentCompany.email || '',
        website: currentCompany.website || '',
        contact_person_name: currentCompany.contact_person_name || '',
        contact_person_title: currentCompany.contact_person_title || '',
        contact_person_phone: currentCompany.contact_person_phone || '',
        contact_person_email: currentCompany.contact_person_email || '',

        time_zone: currentCompany.time_zone || 'America/New_York',
        default_currency: currentCompany.default_currency || 'USD',
        default_payment_terms: currentCompany.default_payment_terms || 30,
        invoice_prefix: currentCompany.invoice_prefix || 'INV',
        quote_prefix: currentCompany.quote_prefix || 'QUO'
      });
    }
  }, [currentCompany]);

  // Set default category to first available category
  useEffect(() => {
    if (!selectedCategory) {
      const availableCategories = [
        {
          id: 'user-management',
          label: 'User Management',
          icon: <GroupIcon />,
          description: 'Manage users, roles, and permissions'
        },
        {
          id: 'profile-management',
          label: 'Profile Management',
          icon: <PersonIcon />,
          description: 'Update your personal profile and avatar'
        },
        {
          id: 'company-info',
          label: 'Company Information',
          icon: <BusinessIcon />,
          description: 'Basic company details and information'
        },
        {
          id: 'business-address',
          label: 'Business Address',
          icon: <HomeIcon />,
          description: 'Physical location and mailing address'
        },
        {
          id: 'contact-info',
          label: 'Contact Information',
          icon: <PhoneIcon />,
          description: 'Phone, email, and website details'
        },
        {
          id: 'primary-contact',
          label: 'Primary Contact',
          icon: <PersonIcon />,
          description: 'Main point of contact person'
        },
        {
          id: 'business-settings',
          label: 'Business Settings',
          icon: <SettingsIcon />,
          description: 'Currency, payment terms, and preferences'
        },
        {
          id: 'delete-company',
          label: 'Company Control',
          icon: <AdminIcon />,
          description: 'Deactivate, reactivate, or delete company'
        }
      ].filter(category => {
        const categoryPermissionMap = {
          'company-info': 'company_information',
          'business-address': 'business_address', 
          'contact-info': 'contact_information',
          'primary-contact': 'primary_contact',
    
          'business-settings': 'business_settings'
        };
        
        // Special handling for company control - only super admin can access
        if (category.id === 'delete-company') {
          return userRole === 'super_admin';
        }
        
        // Special handling for user management - check if user has access to any user management pages
        if (category.id === 'user-management') {
          return canAccessPage('active_users') || canAccessPage('invitations') || canAccessPage('roles_permissions');
        }
             
        const permissionKey = categoryPermissionMap[category.id];
        if (permissionKey) {
          return canAccessPage(permissionKey);
        }
        return true;
      });
      
      if (availableCategories.length > 0) {
        setSelectedCategory(availableCategories[0].id);
      }
    }
  }, [selectedCategory, canAccessPage, userRole]);

  // Clear validation errors when switching sections
  useEffect(() => {
    setValidationErrors({});
  }, [selectedCategory]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setCompanyInfo(prev => {
      const updated = { ...prev, [name]: value };
      if (name === 'country') {
        updated.state = '';
      }
      return updated;
    });
    
    // Clear validation error for this field
    if (validationErrors[name]) {
      setValidationErrors(prev => {
        const updated = { ...prev };
        delete updated[name];
        return updated;
      });
    }
  };

  const handleSave = async () => {
    if (selectedCategory === 'profile-management') {
      await handleProfileSave();
      return;
    }

    if (!validateCurrentSection()) {
      setError('Please fill in all required fields');
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(false);

    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        throw new Error("User not authenticated");
      }

      const companyData = { ...companyInfo };
      Object.keys(companyData).forEach(key => {
        if (companyData[key] === '') {
          delete companyData[key];
        }
      });

      const { data, error } = await supabase
        .from('companies')
        .update(companyData)
        .eq('id', currentCompany.id)
        .select()
        .single();

      if (error) throw error;

      setSuccess(true);
      setTimeout(() => setSuccess(false), 5000);
      if (onCompanyUpdated) {
        onCompanyUpdated(data);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleProfileSave = async () => {
    if (!profileData.first_name.trim() || !profileData.last_name.trim()) {
      setError('First name and last name are required');
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(false);

    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        throw new Error("User not authenticated");
      }

      const { error } = await supabase
        .from('profiles')
        .upsert({
          id: user.id,
          ...profileData
        });

      if (error) throw error;

      setSuccess(true);
      setTimeout(() => setSuccess(false), 5000);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const renderContent = () => {
    switch (selectedCategory) {
      case 'company-info':
        return (
          <CompanyInformation
            companyInfo={companyInfo}
            handleInputChange={handleInputChange}
            loading={loading}
            requiredFields={requiredFields}
            validationErrors={validationErrors}
          />
        );
      case 'business-address':
        return (
          <BusinessAddress
            companyInfo={companyInfo}
            handleInputChange={handleInputChange}
            loading={loading}
            requiredFields={requiredFields}
            validationErrors={validationErrors}
          />
        );
      case 'contact-info':
        return (
          <ContactInformation
            companyInfo={companyInfo}
            handleInputChange={handleInputChange}
            loading={loading}
            requiredFields={requiredFields}
            validationErrors={validationErrors}
          />
        );
      case 'primary-contact':
        return (
          <PrimaryContact
            companyInfo={companyInfo}
            handleInputChange={handleInputChange}
            loading={loading}
            requiredFields={requiredFields}
            validationErrors={validationErrors}
          />
        );

      case 'business-settings':
        return (
          <BusinessSettings
            companyInfo={companyInfo}
            handleInputChange={handleInputChange}
            loading={loading}
            requiredFields={requiredFields}
            validationErrors={validationErrors}
          />
        );
      case 'user-management':
        return <UsersSettings />;
      case 'profile-management':
        return <ProfileManagement onProfileChange={setProfileData} />;
      case 'delete-company':
        return <DeleteCompany />;
      default:
        return null;
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: theme.palette.background.default }}>
      <Container disableGutters maxWidth="xl" sx={{ px: 3, py: 4 }}>
        <Fade in timeout={800}>
          <Box>
            {/* Enhanced Page Header */}
            <Card sx={{
              borderRadius: 3,
              mt: 5,
              mb: 4,
              border: 'none',
              background: 'white',
              boxShadow: 'none',
              borderTop: '3px solid #0277BD',
              position: 'relative',
              overflow: 'hidden',
              '&:hover': {
                boxShadow: `0 4px 20px ${alpha('#0277BD', 0.15)}`,
              },
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            }}>
              <CardContent sx={{ p: 4 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, mb: 2 }}>
                  <Box sx={{
                    p: 2.5,
                    borderRadius: 3,
                    background: alpha('#0277BD', 0.08),
                  }}>
                    <SettingsIcon sx={{ fontSize: 36, color: '#0277BD' }} />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="h3" sx={{
                      fontWeight: 500,
                      color: '#0277BD',
                      mb: 1,
                      fontSize: '2.5rem',
                      letterSpacing: '-0.5px'
                    }}>
                      Company Settings
                    </Typography>
                    <Typography variant="h6" sx={{ 
                      color: 'text.secondary', 
                      fontWeight: 500,
                      fontSize: '1.1rem'
                    }}>
                      {currentCompany?.name || 'Loading...'}
                    </Typography>
                  </Box>
                </Box>
                <Typography variant="body1" sx={{ 
                  color: 'text.secondary', 
                  maxWidth: 700, 
                  lineHeight: 1.6,
                  fontSize: '1.05rem',
                  fontWeight: 500
                }}>
                  Manage your company information, business settings, and contact details with comprehensive 
                  controls and instant updates for streamlined business operations.
                </Typography>
              </CardContent>
            </Card>

            {/* Main Layout */}
            <Box sx={{ display: 'flex', gap: 4, minHeight: '70vh' }}>
              {/* Left Sidebar */}
              <Paper sx={{
                width: 280,
                borderRadius: 3,
                border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                background: `linear-gradient(135deg, ${alpha(theme.palette.grey[50], 0.9)}, ${alpha(theme.palette.grey[100], 0.9)})`,
                boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.15)}`,
                overflow: 'hidden',
                transition: 'all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
                animation: 'slideInUp 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                '@keyframes slideInUp': {
                  '0%': {
                    transform: 'translateY(100%)',
                    opacity: 0,
                  },
                  '100%': {
                    transform: 'translateY(0)',
                    opacity: 1,
                  }
                },
                '&:hover': {
                  transform: 'translateY(-8px)',
                  boxShadow: `0 20px 40px ${alpha(theme.palette.primary.main, 0.25)}`,
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`
                }
              }}>
                <List sx={{ p: 2 }}>
                  {categories.map((category) => (
                    <ListItem key={category.id} disablePadding sx={{ mb: 1 }}>
                      <ListItemButton
                        selected={selectedCategory === category.id}
                        onClick={() => setSelectedCategory(category.id)}
                        sx={{
                          borderRadius: 3,
                          py: 1.5,
                          px: 2,
                          transition: 'all 0.2s cubic-bezier(0.4, 0.0, 0.2, 1)',
                          color: 'text.primary',
                          '& .MuiListItemIcon-root': {
                            color: 'text.secondary'
                          },
                          '& .MuiListItemText-primary': {
                            color: 'text.primary',
                            fontWeight: 500
                          },
                          '& .MuiListItemText-secondary': {
                            color: 'text.secondary',
                            fontWeight: 400
                          },
                          '&.Mui-selected': {
                            bgcolor: alpha(theme.palette.primary.main, 0.08),
                            border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                            background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)}, ${alpha(theme.palette.primary.main, 0.04)})`,
                            '& .MuiListItemIcon-root': {
                              color: 'primary.main'
                            },
                            '& .MuiListItemText-primary': {
                              color: 'primary.main',
                              fontWeight: 500
                            },
                            '& .MuiListItemText-secondary': {
                              color: 'text.secondary',
                              fontWeight: 400
                            },
                            '&:hover': {
                              bgcolor: alpha(theme.palette.primary.main, 0.12),
                            }
                          },
                          '&:hover': {
                            bgcolor: alpha(theme.palette.grey[200], 0.8),
                            transform: 'translateX(4px)',
                          }
                        }}
                      >
                        <ListItemIcon 
                          sx={{ 
                            minWidth: 40
                          }}
                        >
                          {category.icon}
                        </ListItemIcon>
                        <ListItemText 
                          primary={category.label}
                          secondary={category.description}
                          primaryTypographyProps={{
                            fontSize: '0.9rem'
                          }}
                          secondaryTypographyProps={{
                            fontSize: '0.75rem'
                          }}
                        />
                      </ListItemButton>
                    </ListItem>
                  ))}
                </List>
              </Paper>

              {/* Right Content Area */}
              <Paper sx={{
                flex: 1,
                borderRadius: 3,
                border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                background: `linear-gradient(135deg, ${alpha(theme.palette.grey[50], 0.9)}, ${alpha(theme.palette.grey[100], 0.9)})`,
                boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.15)}`,
                overflow: 'hidden',
                transition: 'all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
                animation: 'slideInUp 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                '@keyframes slideInUp': {
                  '0%': {
                    transform: 'translateY(100%)',
                    opacity: 0,
                  },
                  '100%': {
                    transform: 'translateY(0)',
                    opacity: 1,
                  }
                },
                '&:hover': {
                  transform: 'translateY(-8px)',
                  boxShadow: `0 20px 40px ${alpha(theme.palette.primary.main, 0.25)}`,
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`
                }
              }}>
                <Box sx={{ p: 4 }}>
                  {error && (
                    <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
                      {error}
                    </Alert>
                  )}
                  
                  {success && (
                    <Alert 
                      severity="success" 
                      sx={{ mb: 3, borderRadius: 2 }}
                      onClose={() => setSuccess(false)}
                    >
                      Company information updated successfully!
                    </Alert>
                  )}

                  {renderContent()}

                  {/* Save Button - Hide for user-management, company-users, delete-company, and when no edit permissions */}
                  {selectedCategory !== 'user-management' && selectedCategory !== 'company-users' && selectedCategory !== 'delete-company' && hasEditPermissionForCategory() && (
                    <Box sx={{ 
                      mt: 6, 
                      pt: 4, 
                      borderTop: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                      display: 'flex', 
                      justifyContent: 'flex-end' 
                    }}>
                    <Button
                      onClick={handleSave}
                      disabled={loading || isSaveDisabled()}
                      variant="contained"
                      startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                      size="large"
                      sx={{
                        borderRadius: 3,
                        px: 4,
                        py: 1.5,
                        fontSize: '0.9rem',
                        fontWeight: 500,
                        background: loading || isSaveDisabled() ? 'grey.400' : `linear-gradient(${alpha(theme.palette.primary.main, 0.1)}, ${alpha(theme.palette.primary.main, 0.05)})`,
                        border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                        backdropFilter: 'blur(10px)',
                        color: loading || isSaveDisabled() ? 'grey.600' : 'text.secondary',
                        transition: 'all 0.2s cubic-bezier(0.4, 0.0, 0.2, 1)',
                        '&:hover': {
                          background: loading || isSaveDisabled() ? 'grey.400' : `linear-gradient(${alpha(theme.palette.primary.main, 0.15)}, ${alpha(theme.palette.primary.main, 0.08)})`,
                          transform: loading || isSaveDisabled() ? 'none' : 'translateY(-1px)'
                        },
                        '&:disabled': {
                          background: 'grey.400',
                          color: 'grey.600'
                        }
                      }}
                    >
                      {loading ? 'Saving...' : 'Save Changes'}
                    </Button>
                    </Box>
                  )}
                </Box>
              </Paper>
            </Box>
          </Box>
        </Fade>
      </Container>
    </Box>
  );
};

export default SettingsPage;